import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ShipyardService } from '@service/shipyard.service';
import { Subject, Subscription, lastValueFrom, take, takeUntil } from 'rxjs';
import { NewVesselDialogComponent } from '@components/new-vessel-dialog/new-vessel-dialog.component';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import { Shipyard } from 'icreate/common/models/shipyard';
import { NewShipyardDialogComponent } from '@components/new-shipyard-dialog/new-shipyard-dialog.component';
import { SidebarService } from '@service/sidebar.service';
import { MatDialog } from '@angular/material/dialog';
import { VesselService } from '@service/vessel.service';
import { AccessRequest, AccessRequestStatus } from 'icreate/common/models/access-request';
import { UserPictureService } from '@service/user-picture.service';
import { AccessRequestSocketService } from '@service/access-request-socket.service';
import { LocalStorageService } from '@service/local-storage.service';
import { AuthService } from '@service/auth.service';
import { DeleteConfirmationDialogComponent } from '@components/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { MessageDialogService } from '@service/message-dialog.service';
import { shipyardList } from '@helpers/test-mock-data/shipyard-list';
import { Utils } from '@helpers/utils';

@Component({
    selector: 'app-shipyard',
    templateUrl: './shipyard.component.html',
    styleUrls: ['./shipyard.component.css'],
    standalone: false,
})
export class ShipyardComponent implements OnInit, OnDestroy {
    @ViewChild('otherOptions') otherOptions: ElementRef;

    displayGridView: boolean = true;
    showOtherOptions: boolean = false;

    selectedShipyard: Shipyard;
    parameterSubscription: Subscription;

    isLoading: boolean = false;

    vesselsEditors: { [key: string]: { name; image; userOid }[] } = {};
    destroySubscriptions$: Subject<void> = new Subject();
    private shipyardsNames: string[];
    private vesselNames: { [key: string]: string[] };

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        if (!this.otherOptions?.nativeElement.contains(event.target) && this.showOtherOptions) {
            this.showOtherOptions = false;
        }
    }

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private shipyardService: ShipyardService,
        private vesselService: VesselService,
        private sidebarService: SidebarService,
        private accessRequestSocketService: AccessRequestSocketService,
        private userPictureService: UserPictureService,
        private localStorageService: LocalStorageService,
        private dialog: MatDialog,
        private authService: AuthService,
        private messageDialogService: MessageDialogService
    ) {}

    ngOnInit(): void {
        this.startSubscriptions();
        this.parameterSubscription = this.route.params.subscribe(async params => {
            this.isLoading = true;
            const shipyardId = params['shipyardId'];
            const shipyardList = await this.shipyardService.getList();
            this.selectedShipyard = shipyardList?.find(s => s.id === shipyardId);

            if (!this.selectedShipyard) {
                this.router.navigateByUrl('home');
                return;
            }
            this.vesselService.setActiveVessel(null);
            this.shipyardService.setSelectedShipyard(this.selectedShipyard.name);

            this.shipyardsNames = shipyardList.map(shipyard => shipyard.name);
            this.vesselNames = shipyardList.reduce((acc, { id, vessels }) => {
                acc[id] = vessels.map(vessel => vessel.name);
                return acc;
            }, {});

            if (this.selectedShipyard.vessels.length > 0) {
                await Promise.all(
                    this.selectedShipyard.vessels.map(async vessel => {
                        if (
                            vessel.projectStatus === ProjectStatus.Editing ||
                            vessel.projectStatus === ProjectStatus.PendingApproval ||
                            vessel.projectStatus === ProjectStatus.Approved
                        ) {
                            this.vesselsEditors[vessel.id] = [];
                            await this.checkAccessRequests(vessel);
                        }
                    })
                );
            }

            this.sidebarService.updateSelectedShipyard(this.selectedShipyard.id);
            this.displayGridView = this.localStorageService.isVesselAndShipyardGridView(this.selectedShipyard.id);
            this.isLoading = false;
        });
    }

    ngOnDestroy(): void {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }

    private async checkAccessRequests(vessel: Vessel) {
        const editors: { userOid: string; userName: string }[] = [];
        const accessRequestsIds = new Set();
        editors.push({ userOid: vessel.ownerOid, userName: vessel.ownerName });
        if (vessel.VesselAccessRequests?.length > 0) {
            vessel.VesselAccessRequests?.map(accessRequest => {
                if (accessRequest.requestStatus === AccessRequestStatus.Editing) {
                    if (!accessRequestsIds.has(accessRequest.id)) {
                        editors.push({
                            userOid: accessRequest.userOid,
                            userName: accessRequest.userName,
                        });
                        accessRequestsIds.add(accessRequest.id);
                    }
                }
            });
        }

        for await (const editor of editors) {
            let { userOid, userName } = editor;
            const editorPicture = await this.userPictureService.getUserPicture(userOid, userName);
            this.vesselsEditors[vessel.id].push({
                image: editorPicture,
                name: userName,
                userOid,
            });
        }
    }

    async createVessel() {
        const shipyardList = await this.shipyardService.getList();
        const dialogRef = this.dialog.open(NewVesselDialogComponent, {
            width: '474px',
            data: {
                selectedShipyardId: this.selectedShipyard.id,
                shipyardList,
            },
        });
        const vesselData = await lastValueFrom(dialogRef.afterClosed());
        if (vesselData) {
            this.isLoading = true;
            try {
                const userName = this.authService.getCurrentUser().name;
                vesselData.userName = userName;
                await this.vesselService.create(vesselData);
                this.isLoading = false;
                this.messageDialogService.openMessage('recent.vessel.create.notification.title', {
                    number: vesselData.number,
                    name: vesselData.name,
                });
            } catch (err) {
                console.error(err);
                this.isLoading = false;
            }
        }
    }

    async deleteShipyard() {
        const dialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
            width: '474px',
            data: {
                title: 'shipyard.delete.dialog.title',
                name: this.selectedShipyard.name,
                message1: 'shipyard.delete.dialog.message.1',
                message2: 'shipyard.delete.dialog.message.2',
                cancelButton: 'shipyard.delete.dialog.button.cancel',
                buttonDelete: 'shipyard.delete.dialog.button.delete',
                inputLegend: 'shipyard.delete.dialog.input.legend',
            },
        });
        const shouldDelete = await lastValueFrom(dialogRef.afterClosed());

        if (shouldDelete) {
            this.isLoading = true;
            try {
                await this.shipyardService.delete(this.selectedShipyard.id);
                this.router.navigateByUrl('home');
            } catch (err) {
                console.error(err);
            }
            this.isLoading = false;
            this.router.navigateByUrl(`home`);
        }
    }

    async duplicateShipyard() {
        this.isLoading = true;
        try {
            const currentNames = shipyardList.map(shipyard => shipyard.name);
            const takenNames = new Set<string>(currentNames);
            const newName = Utils.nextDuplicateName(this.selectedShipyard.name, takenNames);

            const dialogRef = this.dialog.open(NewShipyardDialogComponent, {
                width: '474px',
                data: { shipyardsNames: this.shipyardsNames, name: newName, isDuplicating: true },
            });
            const shipyardData = await lastValueFrom(dialogRef.afterClosed());
            if (shipyardData) {
                this.isLoading = true;
                const { name, iApiId } = shipyardData;
                try {
                    const newShipyard = structuredClone(this.selectedShipyard);
                    newShipyard.name = name;
                    delete newShipyard.id;
                    await this.shipyardService.duplicate(newShipyard);

                    this.isLoading = false;
                    this.messageDialogService.openMessage('recent.shipyard.create.notification.title', {
                        name,
                    });
                } catch (err) {
                    console.error(err);
                    this.isLoading = false;
                }
            }
        } catch (err) {
            console.error(err);
        }
        this.isLoading = false;
    }

    openMoreOptions() {
        this.showOtherOptions = !this.showOtherOptions;
    }

    async editShipyard() {
        const dialogRef = this.dialog.open(NewShipyardDialogComponent, {
            width: '474px',
            data: {
                name: this.selectedShipyard.name,
                shipyardsNames: this.shipyardsNames,
            },
        });
        const updatedData = await lastValueFrom(dialogRef.afterClosed());
        if (updatedData) {
            this.isLoading = true;
            const { name } = updatedData;
            try {
                await this.shipyardService.update(name, this.selectedShipyard);
                this.selectedShipyard.name = name;
            } catch (err) {
                console.error(err);
            }
            this.isLoading = false;
        }
    }

    private startSubscriptions() {
        this.accessRequestSocketService
            .startedEditing$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                if (this.vesselsEditors[accessRequest.vesselId]) {
                    const editorPicture = await this.userPictureService.getUserPicture(
                        accessRequest.userOid,
                        accessRequest.userName
                    );
                    this.vesselsEditors[accessRequest.vesselId].push({
                        image: editorPicture,
                        name: accessRequest.userName,
                        userOid: accessRequest.userOid,
                    });
                }
            });

        this.accessRequestSocketService
            .finishedEditing$()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (accessRequest: AccessRequest) => {
                if (this.vesselsEditors[accessRequest.vesselId]) {
                    let indexToDelete = this.vesselsEditors[accessRequest.vesselId].findIndex(
                        editor => editor.userOid === accessRequest.userOid
                    );
                    if (indexToDelete !== -1) this.vesselsEditors[accessRequest.vesselId].splice(indexToDelete, 1);
                }
            });

        this.shipyardService
            .listUpdatedSubject()
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async () => {
                const shipyardList = this.shipyardService.getUpdatedList();
                this.shipyardsNames = shipyardList.map(shipyard => shipyard.name);
                this.vesselNames = shipyardList.reduce((acc, { id, vessels }) => {
                    acc[id] = vessels.map(vessel => vessel.name);
                    return acc;
                }, {});
            });
    }

    viewModeToggle(shouldDisplayGrid: boolean) {
        this.displayGridView = shouldDisplayGrid;
        this.localStorageService.setShipyardViewMode(this.selectedShipyard.id, this.displayGridView);
    }
}
